import { Globe, Menu } from "lucide-react";
import { useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { MemberPanel } from "./MemberPanel";
import { AgentLoginDialog } from "./AgentLoginDialog";
import { useDesign, useSite, useSitePath } from "../context/SiteContext";

const LANGS = [["1", "English"], ["51", "Français"], ["57", "Deutsch"], ["66", "Italiano"], ["80", "Português"], ["108", "Español"]];

const LanguageSelect = () => {
  const [lang, setLang] = useState(localStorage.getItem("eres-lang") || "1");
  return (
    <Select value={lang} onValueChange={(v) => { setLang(v); localStorage.setItem("eres-lang", v); }}>
      <SelectTrigger className="h-9 w-[130px] rounded-full border-brand/15 bg-transparent text-sm text-ink" data-testid="language-select">
        <Globe className="h-4 w-4 text-brand" strokeWidth={1.5} />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LANGS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`language-option-${v}`}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

const NavLinks = ({ onAgent, className }) => {
  const sp = useSitePath();
  return (
    <nav className={className}>
      <NavLink to={sp("/")} end className="nav-link" data-testid="nav-home">Home</NavLink>
      <NavLink to={sp("/find-reservation")} className="nav-link" data-testid="nav-find-reservation">Find Reservation</NavLink>
      <button className="nav-link text-left" onClick={onAgent} data-testid="nav-agent-login">Agent Login</button>
    </nav>
  );
};

export const Header = () => {
  const [agent, setAgent] = useState(false);
  const brand = useSite()?.brand;
  const sp = useSitePath();
  const { slug } = useParams();
  const centered = useDesign() === "boutique";
  return (
    <header className="sticky top-0 z-50 border-b border-brand/10 bg-sand/90 backdrop-blur-xl" data-testid="site-header">
      <div className={`container-x flex items-center justify-between gap-6 ${centered ? "h-24 lg:grid lg:grid-cols-[1fr_auto_1fr]" : "h-20"}`}>
        {centered && <NavLinks onAgent={() => setAgent(true)} className="hidden items-center gap-8 lg:flex" />}
        <Link to={sp("/")} className={`flex min-w-0 items-center gap-3 ${centered ? "lg:flex-col lg:gap-1 lg:text-center" : ""}`} data-testid="client-logo">
          {brand?.logo_url ? (
            <img src={brand.logo_url} alt={brand.name} className={`h-12 w-auto object-contain ${centered ? "rounded-md bg-paper/90 p-1" : ""}`} data-testid="client-logo-img" />
          ) : (
            <span className="grid h-11 w-11 place-items-center rounded-full bg-brand font-serif text-xl text-sand">{brand?.name?.[0]}</span>
          )}
          <span className="hidden leading-tight sm:block">
            <span className="block font-serif text-lg text-ink" data-testid="brand-name">{brand?.name}</span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/50">{brand?.tagline}</span>
          </span>
        </Link>
        {!centered && <NavLinks onAgent={() => setAgent(true)} className="hidden items-center gap-8 lg:flex" />}
        <div className={`flex items-center gap-3 ${centered ? "lg:justify-end" : ""}`}>
          <div className="hidden md:block"><LanguageSelect /></div>
          {slug === "tafaria" && <MemberPanel />}
          <Sheet>
            <SheetTrigger asChild>
              <button className="rounded-full p-2 hover:bg-brand-light lg:hidden" data-testid="mobile-menu-trigger"><Menu className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent className="bg-sand" data-testid="mobile-menu">
              <NavLinks onAgent={() => setAgent(true)} className="mt-10 flex flex-col gap-6 text-lg" />
              <div className="mt-8"><LanguageSelect /></div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <AgentLoginDialog open={agent} onOpenChange={setAgent} />
    </header>
  );
};
